import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Chip, Divider, FormControl, Grid, InputLabel, MenuItem, Pagination, Select, TextField, Typography } from '@mui/material'
import CategoryIcon from '@mui/icons-material/Category'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import ScaleIcon from '@mui/icons-material/Scale'
import StraightenIcon from '@mui/icons-material/Straighten'
import Swal from 'sweetalert2'

import { getConfecciones, getGruposConfeccion, getGruposVariedad } from '../services/apis'
import { LoadingMaestro } from '../components/LoadingMaestro'
import { normalizarArray, PAGE_SIZE } from '../utils/apiResponse'

const getApiErrorMessage = (error, fallback) => {
    const message = error.response?.data?.message

    if (Array.isArray(message)) {
        return message.join('\n')
    }

    return message || fallback
}

export const Confecciones = () => {
    const [confecciones, setConfecciones] = useState([])
    const [gruposConfeccion, setGruposConfeccion] = useState([])
    const [gruposVariedad, setGruposVariedad] = useState([])
    const [busqueda, setBusqueda] = useState('')
    const [grupoSeleccionado, setGrupoSeleccionado] = useState('todos')
    const [pagina, setPagina] = useState(1)
    const [loading, setLoading] = useState(true)

    const cargarDatos = useCallback(async () => {
        setLoading(true)
        try {
            const [confeccionesResponse, gruposConfeccionResponse, gruposVariedadResponse] = await Promise.all([
                getConfecciones(),
                getGruposConfeccion(),
                getGruposVariedad(),
            ])
            const confeccionesData = normalizarArray(confeccionesResponse, 'confecciones')

            setConfecciones(confeccionesData)
            setGruposConfeccion(normalizarArray(gruposConfeccionResponse, 'gruposConfeccion'))
            setGruposVariedad(normalizarArray(gruposVariedadResponse, 'gruposVariedad'))
        } catch (error) {
            console.error('Error al cargar confecciones:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al cargar la informacion de confecciones'),
                timer: 2500,
                showConfirmButton: false,
            })
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        cargarDatos()
    }, [cargarDatos])

    useEffect(() => {
        setPagina(1)
    }, [busqueda, grupoSeleccionado])

    const gruposPorCodigo = useMemo(() => (
        gruposConfeccion.reduce((acc, grupo) => {
            acc[grupo.codGrupoConfeccion] = grupo.desGrupoConfeccion
            return acc
        }, {})
    ), [gruposConfeccion])

    const confeccionesFiltradas = useMemo(() => {
        const texto = busqueda.trim().toLowerCase()

        return confecciones.filter((confeccion) => {
            const coincideBusqueda = !texto
                || confeccion.codConfeccion?.toLowerCase().includes(texto)
                || confeccion.desConfeccion?.toLowerCase().includes(texto)
            const coincideGrupo = grupoSeleccionado === 'todos'
                || (confeccion.codGrupoConfeccion || 'sin-grupo') === grupoSeleccionado

            return coincideBusqueda && coincideGrupo
        })
    }, [busqueda, confecciones, grupoSeleccionado])

    const totalPaginas = Math.max(Math.ceil(confeccionesFiltradas.length / PAGE_SIZE), 1)
    const confeccionesPaginadas = useMemo(() => {
        const inicio = (pagina - 1) * PAGE_SIZE

        return confeccionesFiltradas.slice(inicio, inicio + PAGE_SIZE)
    }, [confeccionesFiltradas, pagina])

    return (
        <Grid
            container
            direction="row"
            size={{ xs: 12 }}
            sx={{
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <Grid
                container
                direction="row"
                size={{ xs: 10 }}
                sx={{
                    justifyContent: "start",
                    alignItems: "start",
                    mt: 3,
                }}
                className='fade-in ajustes-compact'
            >
                {loading ? (
                    <LoadingMaestro label="Cargando maestros Business Central..." />
                ) : (
                <>
                <Grid
                    container
                    direction="row"
                    size={{ xs: 12 }}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                    spacing={2}
                >
                    <Grid container direction="column" size={{ xs: 12 }} sx={{ alignItems: "start" }}>
                        <Typography variant='h4'>
                            Maestros Business Central
                        </Typography>

                        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                            Maestros sincronizados de formatos, grupos de confeccion y grupos de variedad.
                        </Typography>
                    </Grid>

                    <Grid container size={{ xs: 12, sm: 4 }} className='card-main' sx={{ p: 1.5, alignItems: "center" }}>
                        <Inventory2Icon color="primary" sx={{ mr: 1 }} />
                        <Box>
                            <Typography variant="caption">Confecciones</Typography>
                            <Typography variant="h5">{confecciones.length}</Typography>
                        </Box>
                    </Grid>

                    <Grid container size={{ xs: 12, sm: 4 }} className='card-main' sx={{ p: 1.5, alignItems: "center" }}>
                        <CategoryIcon color="primary" sx={{ mr: 1 }} />
                        <Box>
                            <Typography variant="caption">Grupos confeccion</Typography>
                            <Typography variant="h5">{gruposConfeccion.length}</Typography>
                        </Box>
                    </Grid>

                    <Grid container size={{ xs: 12, sm: 4 }} className='card-main' sx={{ p: 1.5, alignItems: "center" }}>
                        <LocalOfferIcon color="primary" sx={{ mr: 1 }} />
                        <Box>
                            <Typography variant="caption">Grupos variedad</Typography>
                            <Typography variant="h5">{gruposVariedad.length}</Typography>
                        </Box>
                    </Grid>

                    <Divider />
                </Grid>

                <Grid
                    container
                    direction="row"
                    size={{ xs: 12 }}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        my: 2,
                    }}
                    spacing={2}
                >
                    <Grid container size={{ xs: 12, md: 7 }}>
                        <TextField
                            label="Buscar confeccion"
                            value={busqueda}
                            onChange={(event) => setBusqueda(event.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid container size={{ xs: 12, md: 5 }}>
                        <FormControl fullWidth>
                            <InputLabel>Grupo de confeccion</InputLabel>
                            <Select
                                label="Grupo de confeccion"
                                value={grupoSeleccionado}
                                onChange={(event) => setGrupoSeleccionado(event.target.value)}
                            >
                                <MenuItem value="todos">Todos</MenuItem>
                                <MenuItem value="sin-grupo">Sin grupo</MenuItem>
                                {gruposConfeccion.map((grupo) => (
                                    <MenuItem key={grupo.codGrupoConfeccion} value={grupo.codGrupoConfeccion}>
                                        {grupo.codGrupoConfeccion} - {grupo.desGrupoConfeccion}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <Grid container direction="row" size={{ xs: 12 }} sx={{ mt: 1, alignItems: "center" }}>
                    <Typography variant="h5">
                        Confecciones
                    </Typography>
                </Grid>

                <Grid
                    container
                    direction="row"
                    size={{ xs: 12 }}
                    sx={{
                        justifyContent: "start",
                        alignItems: "stretch",
                    }}
                    spacing={2}
                >
                    {confeccionesPaginadas.map((confeccion) => (
                        <Grid key={confeccion.codConfeccion} container direction="column" size={{ xs: 12, md: 6, xl: 4 }} className='card-main' sx={{ p: 2, gap: 1.5 }}>
                            <Grid container direction="row" size={{ xs: 12 }} sx={{ justifyContent: "space-between", alignItems: "start", gap: 1 }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="h6" sx={{ lineHeight: 1.15 }}>
                                        {confeccion.desConfeccion}
                                    </Typography>

                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {confeccion.codConfeccion}
                                    </Typography>
                                </Box>

                                <Chip
                                    size="small"
                                    label={confeccion.codGrupoConfeccion || 'Sin grupo'}
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid container direction="row" size={{ xs: 12 }} sx={{ gap: 1 }}>
                                <Chip
                                    size="small"
                                    icon={<ScaleIcon />}
                                    label={`${confeccion.peso ?? '-'} kg`}
                                    color="primary"
                                    variant="outlined"
                                />

                                <Chip
                                    size="small"
                                    icon={<StraightenIcon />}
                                    label={`${confeccion.unidades ?? '-'} uds`}
                                    variant="outlined"
                                />
                            </Grid>

                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {confeccion.codGrupoConfeccion
                                    ? gruposPorCodigo[confeccion.codGrupoConfeccion] || 'Grupo no identificado'
                                    : 'No asignada a ningun grupo de confeccion'}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>

                <Grid
                    container
                    direction="row"
                    size={{ xs: 12 }}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 2,
                        gap: 1,
                    }}
                >
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Mostrando {confeccionesPaginadas.length} de {confeccionesFiltradas.length} confecciones
                    </Typography>

                    <Pagination
                        count={totalPaginas}
                        page={pagina}
                        onChange={(_, value) => setPagina(value)}
                        color="primary"
                    />
                </Grid>

                <Grid container direction="row" size={{ xs: 12 }} sx={{ mt: 3 }} spacing={2}>
                    <Grid container direction="column" size={{ xs: 12, lg: 6 }} sx={{ alignItems: "start" }}>
                        <Typography variant="h5">
                            Grupos de confeccion
                        </Typography>

                        <Grid container direction="row" size={{ xs: 12 }} spacing={1} sx={{ mt: 1 }}>
                            {gruposConfeccion.map((grupo) => (
                                <Grid key={grupo.codGrupoConfeccion} container size={{ xs: 12, sm: 6 }} className='card-main' sx={{ p: 1.5, justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography variant="body1">{grupo.desGrupoConfeccion}</Typography>
                                    <Chip size="small" label={grupo.codGrupoConfeccion} variant="outlined" />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    <Grid container direction="column" size={{ xs: 12, lg: 6 }} sx={{ alignItems: "start" }}>
                        <Typography variant="h5">
                            Grupos de variedad
                        </Typography>

                        <Grid container direction="row" size={{ xs: 12 }} spacing={1} sx={{ mt: 1 }}>
                            {gruposVariedad.map((grupo) => (
                                <Grid key={grupo.codGrupoVariedad} container size={{ xs: 12, sm: 6 }} className='card-main' sx={{ p: 1.5, justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography variant="body1">{grupo.desGrupoVariedad}</Typography>
                                    <Chip size="small" label={grupo.codGrupoVariedad} variant="outlined" />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
                </>
                )}
            </Grid>
        </Grid>
    )
}
